import React, { useEffect, useRef, useState } from 'react';
import { fetchJson } from '../../apiBase';
import './LearningDevelopment.css';

const ACTIVITY_NAMES = {
  multiple_choice: 'Soru etkinliği', memory_matching: 'Eşini bul', pattern_recognition: 'Deseni yakala',
  digit_recall: 'Sayıları hatırla', odd_one_out: 'Farklı olanı bul', reflex: 'Refleks etkinliği',
  cargo_sorting: 'Kargoları ayır', turkish_l1_tour: 'Türkçe etkinliği',
};
function dateLabel(value) {
  if (!value) return '';
  const date = new Date(value.length === 10 ? `${value}T12:00:00+03:00` : value);
  return Number.isNaN(date.getTime()) ? '' : new Intl.DateTimeFormat('tr-TR', {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/Istanbul',
  }).format(date);
}
async function loadSummary(studentId, extra = '') {
  const response = await fetchJson(`/parent/student360/learning-summary?student_id=${encodeURIComponent(studentId)}${extra}`);
  if (!response?.ok) throw new Error('summary_unavailable');
  const body = await response.json();
  if (String(body.student_id) !== String(studentId)) throw new Error('student_mismatch');
  return body;
}
function Notice({ source, loading, children }) {
  if (loading) return <p role="status">Bilgiler yükleniyor…</p>;
  if (source?.state !== 'ready') return <p role="alert">{children}</p>;
  return null;
}

export default function LearningDevelopment({ studentId, active, refreshKey }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [pageError, setPageError] = useState(false);
  const generation = useRef(0);
  const cached = useRef(null);
  const cacheKey = `${studentId}:${refreshKey}`;
  useEffect(() => {
    const request = ++generation.current;
    setPageLoading(false);
    if (cached.current?.key !== cacheKey) {
      cached.current = null;
      setSummary(null); setLoading(true); setExpanded(false); setPageError(false); setPageLoading(false);
    }
    if (active && !cached.current) {
      setLoading(true);
      loadSummary(studentId).then((body) => {
        if (generation.current !== request) return;
        cached.current = { key: cacheKey, body };
        setSummary(body); setLoading(false);
      }).catch(() => {
        if (generation.current !== request) return;
        setSummary({ mini_tasks: { state: 'error' }, weekly_reading: { state: 'error' } });
        setLoading(false);
      });
    }
    return () => { generation.current += 1; };
  }, [studentId, active, cacheKey]);

  const mini = summary?.mini_tasks;
  const reading = summary?.weekly_reading;
  const readings = reading?.recent || [];
  async function showMore() {
    const request = generation.current;
    setPageLoading(true); setPageError(false);
    try {
      const body = await loadSummary(studentId, `&term=${reading.term}&reading_offset=${readings.length}&reading_limit=50`);
      if (generation.current !== request) return;
      if (body.weekly_reading?.state !== 'ready') throw new Error('reading_unavailable');
      setSummary((current) => ({ ...current, weekly_reading: {
        ...body.weekly_reading, recent: [...new Map([...current.weekly_reading.recent, ...body.weekly_reading.recent].map((item) => [item.id, item])).values()],
      } }));
      setExpanded(true);
    } catch (_) {
      if (generation.current === request) setPageError(true);
    } finally {
      if (generation.current === request) setPageLoading(false);
    }
  }

  return <div className="student360-learning">
    <section className="student360-learning-block" aria-labelledby="student360-mini-heading">
      <header><h3 id="student360-mini-heading"><span aria-hidden="true">🎮 </span>Mini Görevlerim</h3><p>Son 30 gün · Okul + Ev</p></header>
      <Notice source={mini} loading={loading}>Mini Görev bilgileri şu anda alınamıyor.</Notice>
      {!loading && mini?.state === 'ready' && <>
        <div className="student360-learning-metrics">
          <div><strong>{mini.active_days}</strong><span>Etkinlik yapılan gün</span></div>
          <div><strong>{mini.completed_main_rounds}</strong><span>Tamamlanan etkinlik</span></div>
        </div>
        {mini.completed_main_rounds === 0 ? <p>Son 30 günde henüz tamamlanan Mini Görev bulunmuyor.</p> : <>
          <p className="student360-learning-note">Küçük adımlar, güzel bir öğrenme alışkanlığına dönüşür.</p>
          {!!mini.recent?.length && <ul className="student360-learning-list" aria-label="Son Mini Görev etkinlikleri">
            {mini.recent.map((item) => <li key={item.id}>
              <div><strong>{item.subject || 'Mini Görev'}</strong><span>{ACTIVITY_NAMES[item.activity] || 'Mini Görev etkinliği'}{item.level != null && ` · Level ${item.level}`}</span></div>
              <time dateTime={item.completed_at}>{dateLabel(item.completed_at)}</time>
            </li>)}
          </ul>}
        </>}
      </>}
    </section>
    {reading?.state !== 'not_applicable' && <section className="student360-learning-block" aria-labelledby="student360-reading-heading">
      <header><h3 id="student360-reading-heading"><span aria-hidden="true">📖 </span>Haftalık Okuma Gelişimim</h3><p>Bu eğitim dönemi{reading?.term_label && ` · ${reading.term_label}`}</p></header>
      <Notice source={reading} loading={loading}>Haftalık Okuma bilgileri şu anda alınamıyor.</Notice>
      {!loading && reading?.state === 'ready' && <>
        {reading.assigned_due_count === 0 ? <><p className="student360-learning-ratio">—</p><p>Bu dönem henüz haftalık okuma atanmadı.</p></> : <>
          <p className="student360-learning-ratio">{reading.completed_count} / {reading.assigned_due_count} <span>okuma tamamlandı</span></p>
          <div className="student360-reading-progress"><progress value={reading.completed_count} max={reading.assigned_due_count} aria-label="Atanan okumaların tamamlanma oranı" /><span>%{reading.percentage}</span></div>
          <ul className="student360-learning-list" aria-label="Dönemin haftalık okumaları">
            {(expanded ? readings : readings.slice(0, 3)).map((item) => <li key={item.id}>
              <div><strong>{item.title || 'Haftalık Okuma'}</strong><time dateTime={item.week_start}>{dateLabel(item.week_start)} haftası</time></div>
              <span className={`student360-reading-state${item.status === 'completed' ? ' is-completed' : ''}`}>{item.status === 'completed' ? 'Tamamlandı' : 'Bekliyor'}</span>
            </li>)}
          </ul>
          {pageError && <p role="alert">Diğer okumalar şu anda alınamıyor. Yeniden deneyebilirsin.</p>}
          <div className="student360-learning-controls">
            {!expanded && reading.assigned_due_count > 3 && <button type="button" disabled={pageLoading} onClick={readings.length > 3 ? () => setExpanded(true) : showMore}>{pageLoading ? 'Yükleniyor…' : 'Tümünü göster'}</button>}
            {expanded && reading.has_more && <button type="button" disabled={pageLoading} onClick={showMore}>{pageLoading ? 'Yükleniyor…' : 'Daha fazla yükle'}</button>}
            {expanded && <button type="button" onClick={() => setExpanded(false)}>Daha az göster</button>}
          </div>
        </>}
      </>}
    </section>}
  </div>;
}
